import type { ReactNode } from 'react'
import RogerioSpeech from '../RogerioSpeech'
import { ActionLink, PaperCard } from './Workshop' // termo-ok: nome técnico do arquivo de componentes

/**
 * Peças das páginas de leitura (valores, possibilidades, conversa, acompanhamento, passo a passo, profissões),
 * compostas com os tokens e componentes da Oficina ilustrada. Estilos em
 * src/article.css. Cada peça é só estrutura: o texto vem da página.
 */

export function ArticleSection({ id, eyebrow, title, hint, children }: { id: string; eyebrow?: string; title: ReactNode; hint?: ReactNode; children?: ReactNode }) {
  return <section className="of-article-section" aria-labelledby={id}>
    {eyebrow && <p className="eyebrow"><span />{eyebrow}</p>}
    <h2 id={id}>{title}</h2>
    {hint && <p className="of-article-section__hint">{hint}</p>}
    {children}
  </section>
}

export function ArticleList({ ordered = false, children }: { ordered?: boolean; children: ReactNode }) {
  return ordered ? <ol className="of-article-list">{children}</ol> : <ul className="of-article-list">{children}</ul>
}

export function ArticleCards({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={'of-article-cards ' + className}>{children}</div>
}

export function ArticleCard({ tone = 'paper', title, headingLevel = 3, children }: { tone?: 'paper' | 'sky' | 'sage' | 'peach'; title: ReactNode; headingLevel?: 2 | 3; children: ReactNode }) {
  return <PaperCard tone={tone} className="of-article-card">
    {headingLevel === 2 ? <h2>{title}</h2> : <h3>{title}</h3>}
    {children}
  </PaperCard>
}

/** "Você traz / A IA ajuda / Você continua", o mesmo desenho dos exemplos da página inicial. */
export function ArticleExample({ brings, helps, continues }: { brings: string; helps: string; continues: string }) {
  return <dl>
    <div><dt>Você traz</dt><dd>{brings}</dd></div>
    <div><dt>A IA ajuda</dt><dd>{helps}</dd></div>
    <div><dt>Você continua</dt><dd>{continues}</dd></div>
  </dl>
}

export function ArticleSteps({ steps }: { steps: readonly { title: string; body: ReactNode }[] }) {
  return <ol className="of-article-steps">
    {steps.map((step) => <li key={step.title}><div><strong>{step.title}</strong><p>{step.body}</p></div></li>)}
  </ol>
}

export function ArticleFaq({ idPrefix, items }: { idPrefix: string; items: readonly { question: string; answer: string }[] }) {
  return <div className="of-article-faq">
    {items.map((item, index) => <details key={item.question} open={index === 0}>
      <summary id={`${idPrefix}-${index + 1}`}>{item.question}</summary>
      <p>{item.answer}</p>
    </details>)}
  </div>
}

type ClosingLink = { href: string; label: string; external?: boolean }

/**
 * Fechamento de página: o convite principal é o mesmo da página inicial
 * (marcar a primeira conversa na agenda); os links de apoio e a nota em
 * letra menor (horário, o que se combina na conversa, dúvida no WhatsApp)
 * vêm de cada página.
 */
export function ArticleClosing({ eyebrow, title, children, action, secondary, note, rogerio }: {
  eyebrow: string
  title: ReactNode
  children: ReactNode
  action: ClosingLink
  secondary?: ClosingLink | readonly ClosingLink[]
  note?: ReactNode
  rogerio: { pose: string; text: string }
}) {
  const externalProps = (external?: boolean) => (external ? { target: '_blank', rel: 'noopener noreferrer' } : {})
  const secondaries = secondary ? (Array.isArray(secondary) ? secondary : [secondary as ClosingLink]) : []
  return <PaperCard tone="sage" className="of-article-closing">
    <div>
      <p className="eyebrow"><span />{eyebrow}</p>
      <h2>{title}</h2>
      <p>{children}</p>
      <div className="of-article-closing__actions">
        <ActionLink href={action.href} {...externalProps(action.external)}>{action.label}</ActionLink>
        {secondaries.map((link) => <a key={link.href} className="of-article-closing__secondary" href={link.href} {...externalProps(link.external)}>{link.label} →</a>)}
      </div>
      {note && <p className="of-article-closing__note">{note}</p>}
    </div>
    <RogerioSpeech pose={rogerio.pose} layout="beside">{rogerio.text}</RogerioSpeech>
  </PaperCard>
}
